import {Injectable} from '@angular/core';
import { ExtraFee } from '../../models/quote/extra-fee';
import { Observable } from 'rxjs';
import { EnumExtraFeeProcessingMode } from '../../models/enum/enum-extra-fee-processing-mode';
import { EnumExtraFeeType } from '../../models/enum/enum-extra-fee-type';
import { bigToString, parseBig } from '../../utils/math-utils';
import Big from 'big.js';
import { DevicePricingItem } from '../../models/quote/pricing-item/device-pricing-item';
import { PackPricingItem } from '../../models/quote/pricing-item/pack-pricing-item';
import { DeviceConsumablePricingItem } from '../../models/quote/pricing-item/device-consumable-pricing-item';
import { ConsumablePricingItem } from '../../models/quote/pricing-item/consumable-pricing-item';
import { QuoteService } from '../../models/quote/quote-service';
import { EmployeeRoleEnum } from '../../models/employee/employee-role.enum';
import {DeviceConsumableWithSuppPricingItem} from '../../models/quote/pricing-item/device-consumable-with-supp-pricing-item';

@Injectable()
export abstract class ExtraFeeService {

    abstract getExtraFeesByServiceAndCountryAndRole(country: string, role: EmployeeRoleEnum, serviceCode?: string): Observable<ExtraFee[]>;


    private resolveExtraFeeType(extraFee: ExtraFee): string {
        if (extraFee?.type) {
            return extraFee.type;
        }
        switch (extraFee?.processingMode?.code) {
            case EnumExtraFeeProcessingMode.CA_PERCENTAGE:
                return EnumExtraFeeType.PERCENTAGE_EXTRA_FEE;
            case EnumExtraFeeProcessingMode.FIXE:
                return EnumExtraFeeType.EXTRA_FEE;
            default:
                return EnumExtraFeeType.QUANTITY_EXTRA_FEE;
        }
    }


    private resolveExtraFeePercentage(extraFee: ExtraFee): string {
        return extraFee?.percentage ?? extraFee?.uniteAmount;
    }

    getAmount(extraFee: ExtraFee, quoteService: QuoteService) {
        switch (this.resolveExtraFeeType(extraFee)) {
            case EnumExtraFeeType.PERCENTAGE_EXTRA_FEE:
                return bigToString((parseBig(this.resolveExtraFeePercentage(extraFee)).div(new Big(100))).mul(parseBig(quoteService.monthlyAmount)));
            default:
                return extraFee.uniteAmount;
        }
    }

    isManualQuantity(extraFee: ExtraFee): boolean {
        return extraFee?.processingMode?.code === EnumExtraFeeProcessingMode.DEFINED_QUANTITY;
    }

    getQuantity(extraFee: ExtraFee, quoteService: QuoteService) {
        switch (this.resolveExtraFeeType(extraFee)) {
            case EnumExtraFeeType.EXTRA_FEE:
                return 1;
            case EnumExtraFeeType.PERCENTAGE_EXTRA_FEE:
                return this.resolveExtraFeePercentage(extraFee);
            default:
                return this.getQuantityFromProcessingMode(extraFee, quoteService);
        }
    }

    private getQuantityFromProcessingMode(extraFee: ExtraFee, quoteService: QuoteService) {
        let extraFeeQuantity;
        switch (extraFee.processingMode?.code) {
            case EnumExtraFeeProcessingMode.NBR_DEVICES:
                extraFeeQuantity = this.getSumOfQuantitiesOnlyForDevicesAndPack(quoteService);
                break;
            case EnumExtraFeeProcessingMode.NBR_TOTAL_ARTICLES_STOCK:
                extraFeeQuantity = this.getSumOfQuantitiesForAllTypeOfArticles(quoteService);
                break;
            case EnumExtraFeeProcessingMode.NBRE_DE_PORTEURS:
                extraFeeQuantity = quoteService.userInputs?.totalNumberOfHolders ? Number(quoteService.userInputs?.totalNumberOfHolders) : 0;
                break;
            default :
            extraFeeQuantity = extraFee.quantity;
        }
        return extraFeeQuantity;
    }

    getSumOfQuantitiesForAllTypeOfArticles(quoteService: QuoteService) {
        let totalQuantity = 0;
        quoteService.productPricingItems.forEach(pricingItem => {
            if ((pricingItem instanceof DevicePricingItem || pricingItem instanceof PackPricingItem) && pricingItem.userInputs.quantity) {
                totalQuantity += Number(pricingItem.userInputs.quantity);
            }
            if ((pricingItem instanceof DeviceConsumablePricingItem ||
                pricingItem instanceof DeviceConsumableWithSuppPricingItem) && (pricingItem.userInputs.quantity && pricingItem.userInputs.consumableQuantity)) {
                totalQuantity += Number(pricingItem.userInputs.quantity) + Number(pricingItem.userInputs.consumableQuantity);
            }
            if (pricingItem instanceof ConsumablePricingItem && pricingItem.userInputs.consumableQuantity) {
                totalQuantity += Number(pricingItem.userInputs.consumableQuantity);
            }
        });
        return totalQuantity;
    }

    getSumOfQuantitiesOnlyForDevicesAndPack(quoteService: QuoteService) {
        let totalQuantity = 0;
        quoteService.productPricingItems.forEach(pricingItem => {
            if (pricingItem.userInputs.quantity) {
                if (pricingItem instanceof DevicePricingItem || pricingItem instanceof PackPricingItem) {
                    totalQuantity += Number(pricingItem.userInputs.quantity);
                }
                if (pricingItem instanceof DeviceConsumablePricingItem ||
                    pricingItem instanceof DeviceConsumableWithSuppPricingItem) {
                    totalQuantity += Number(pricingItem.userInputs.quantity);
                }
            }
        });
        return totalQuantity;
    }

    getExtraFeeTotal(extraFee: ExtraFee, quoteService: QuoteService) {
        switch (this.resolveExtraFeeType(extraFee)) {
            case EnumExtraFeeType.EXTRA_FEE:
                return Number(extraFee.uniteAmount || 0);
            case EnumExtraFeeType.PERCENTAGE_EXTRA_FEE:
                return Number(Big(this.resolveExtraFeePercentage(extraFee) || 0).div(new Big(100)).mul(quoteService.monthlyAmount));
            default:
                const quantity = this.getQuantityFromProcessingMode(extraFee, quoteService);
                return quantity ? Number(Big(extraFee.uniteAmount || 0).mul(quantity)) : 0;
        }
    }

}
